import React, { Component, PropsWithChildren } from 'react'
import { Popover, PopoverProps, PopoverVirtualElement } from '@mui/material'
import { ContentDefault } from './ui.units'

export interface PopoverGlobalState {
  anchorEl?: Element | (() => Element) | PopoverVirtualElement | (() => PopoverVirtualElement) | null
  popoverProps?: Omit<PopoverProps, 'open'>
  content: React.ReactNode
}

export type OpenPopover = (value: PopoverGlobalState) => void

export type ClosePopover = (reason?: 'backdropClick' | 'escapeKeyDown') => void

export interface PopoverGlobalContext {
  open: OpenPopover
  close: ClosePopover
}

export const PopoverGlobalApi: PopoverGlobalContext = {
  open: () => {
    console.warn('PopoverGlobal provider not found!')
  },
  close: () => {
    console.warn('PopoverGlobal provider not found!')
  }
}

export const PopoverGlobalContext = React.createContext<PopoverGlobalContext>(PopoverGlobalApi)

export const mapPopoverGlobalContext = (context: (state: PopoverGlobalContext) => React.ReactNode) => (
  <PopoverGlobalContext.Consumer>{context}</PopoverGlobalContext.Consumer>
)

interface IProps extends PropsWithChildren {}

export class PopoverGlobalProvider extends Component<IProps, PopoverGlobalState> {
  constructor(props: any) {
    super(props)
    this.state = { anchorEl: null, content: <ContentDefault /> }
  }

  componentDidMount() {
    PopoverGlobalApi.open = this.openPopover
    PopoverGlobalApi.close = this.closePopover
  }

  render() {
    return (
      <PopoverGlobalContext.Provider value={{ open: this.openPopover, close: this.closePopover }}>
        {this.props.children}
        <Popover
          sx={{ '& .MuiPaper-root.MuiPaper-elevation': { overflow: 'hidden' } }}
          anchorEl={this.state.anchorEl}
          open={Boolean(this.state.anchorEl)}
          onClose={(_, reason) => this.closePopover(reason)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          disableScrollLock
          {...this.state.popoverProps}
        >
          {this.state.content}
        </Popover>
      </PopoverGlobalContext.Provider>
    )
  }

  openPopover: OpenPopover = (option) => {
    clearTimeout(this.timer)
    this.setState({ ...option })
  }

  timer: any
  closePopover: ClosePopover = () => {
    clearTimeout(this.timer)
    this.setState({ anchorEl: null }, () => {
      this.timer = setTimeout(() => {
        this.setState({ content: <ContentDefault /> })
      }, 500)
    })
  }
}

export interface IPopoverGlobalContextProps {
  contextPopover: PopoverGlobalContext
}

export const withPopoverGlobalContext = <P extends IPopoverGlobalContextProps>(Component: React.ComponentType<P>) => {
  return (props: Omit<P, keyof IPopoverGlobalContextProps>) => {
    return (
      <PopoverGlobalProvider>
        {mapPopoverGlobalContext((context) => (
          <Component {...(props as P)} contextPopover={context} />
        ))}
      </PopoverGlobalProvider>
    )
  }
}

const PopoverGlobal = {
  Provider: PopoverGlobalProvider,
  Api: PopoverGlobalApi,
  Context: PopoverGlobalContext,
  mapContext: mapPopoverGlobalContext
}

export default PopoverGlobal
